import React, { useState, useEffect } from "react";
import { Ticket, Plus, X, Edit2, ToggleRight, DollarSign, Users, TrendingUp, ScanLine, Zap } from "lucide-react";
import { Link } from "react-router-dom";
import { useAuth } from "../../../context/AuthContext";
import PremiumFeatureGate from "../PremiumFeatureGate";
import QuickTicketSetup from "../CreateEvent/QuickTicketSetup";
import {
  getTicketTypes,
  saveTicketType,
  deactivateTicketType,
  getTicketSales,
} from "../../../services/TicketingService";

const EMPTY_FORM = {
  name: "",
  description: "",
  price: "",
  totalQuantity: "",
  saleStartDate: "",
  saleEndDate: "",
  isActive: true
};

const Ticketing = ({ eventId, eoUserId, eventName }) => {
  const { subscriptionTier } = useAuth();
  const isPro = subscriptionTier === 'pro' || subscriptionTier === 'premium' || subscriptionTier === 'enterprise';

  const [view, setView] = useState("manage"); // "manage" | "sales"
  const [showQuickSetup, setShowQuickSetup] = useState(false);
  const [ticketTypes, setTicketTypes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Sales state
  const [sales, setSales] = useState(null);
  const [salesLoading, setSalesLoading] = useState(false);

  useEffect(() => {
    if (eventId && isPro) loadTicketTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId, isPro]);

  useEffect(() => {
    if (view === "sales" && eventId && eoUserId && !sales) loadSales();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  const loadTicketTypes = async () => {
    setIsLoading(true);
    const result = await getTicketTypes(eventId);
    if (result.success) setTicketTypes(result.ticketTypes);
    setIsLoading(false);
  };

  const loadSales = async () => {
    setSalesLoading(true);
    const result = await getTicketSales(eventId, eoUserId);
    if (result.success) setSales(result);
    setSalesLoading(false);
  };

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setError(null);
    setFormOpen(true);
  };

  const openEdit = (tt) => {
    setEditingId(tt.id);
    setForm({
      name: tt.name || "",
      description: tt.description || "",
      price: tt.price != null ? String(tt.price) : "",
      totalQuantity: tt.totalQuantity != null ? String(tt.totalQuantity) : "",
      saleStartDate: tt.saleStartDate ? formatDateForInput(tt.saleStartDate) : "",
      saleEndDate: tt.saleEndDate ? formatDateForInput(tt.saleEndDate) : "",
      isActive: tt.isActive !== false
    });
    setError(null);
    setFormOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { setError("Name is required."); return; }
    const price = parseFloat(form.price);
    if (isNaN(price) || price < 0) { setError("Enter a valid price (0 for free)."); return; }
    const qty = parseInt(form.totalQuantity);
    if (isNaN(qty) || qty < 1) { setError("Enter a valid quantity (minimum 1)."); return; }

    setSaving(true);
    setError(null);
    const payload = {
      name: form.name.trim(),
      description: form.description.trim(),
      price,
      totalQuantity: qty,
      isActive: form.isActive,
      saleStartDate: form.saleStartDate ? new Date(form.saleStartDate) : null,
      saleEndDate: form.saleEndDate ? new Date(form.saleEndDate) : null
    };
    const result = await saveTicketType(eventId, editingId, payload);
    if (result.success) {
      setFormOpen(false);
      await loadTicketTypes();
    } else {
      setError(result.error || "Failed to save.");
    }
    setSaving(false);
  };

  const handleDeactivate = async (id) => {
    if (!window.confirm("Deactivate this ticket type? It will no longer be sold.")) return;
    await deactivateTicketType(eventId, id);
    await loadTicketTypes();
  };

  const formatDateForInput = (ts) => {
    const d = ts?.toDate ? ts.toDate() : new Date(ts);
    return d.toISOString().slice(0, 10);
  };

  const formatCurrency = (n) => `$${Number(n).toFixed(2)}`;

  const content = (
    <>
    {showQuickSetup && (
      <QuickTicketSetup
        eventId={eventId}
        eventName={eventName}
        onClose={() => setShowQuickSetup(false)}
        onDone={() => { setShowQuickSetup(false); loadTicketTypes(); }}
      />
    )}
    <div className="w-full p-6 sm:p-8">
      <div className="max-w-3xl">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 mb-2">
        <div>
          <h2 className="text-xl font-semibold text-grey-900">Ticketing</h2>
          <p className="text-sm text-grey-500 mt-0.5">
            Create ticket types and sell directly to attendees.
          </p>
        </div>
        <div className="flex bg-grey-100 rounded-lg p-1 flex-shrink-0">
          <button
            onClick={() => setView("manage")}
            className={`px-3 py-1.5 text-sm rounded-md font-medium transition ${view === "manage" ? "bg-white shadow-sm text-grey-900" : "text-grey-500 hover:text-grey-700"}`}
          >
            Manage
          </button>
          <button
            onClick={() => setView("sales")}
            className={`px-3 py-1.5 text-sm rounded-md font-medium transition ${view === "sales" ? "bg-white shadow-sm text-grey-900" : "text-grey-500 hover:text-grey-700"}`}
          >
            Sales
          </button>
        </div>
      </div>

      {/* Action bar */}
      <div className="flex items-center justify-between mb-5">
        {ticketTypes.some(t => t.isActive) ? (
          <div className="flex items-center gap-2 p-2.5 bg-blue-50 border border-blue-200 rounded-xl flex-1 mr-3">
            <Ticket className="w-3.5 h-3.5 text-blue-600 flex-shrink-0" />
            <p className="text-xs text-blue-700 truncate">
              <span className="font-medium">{window.location.origin}/events/{eventId}</span>
            </p>
          </div>
        ) : <div />}
        <div className="flex items-center gap-2 flex-shrink-0">
          <Link
            to={`/eo/events/${eventId}/checkin`}
            className="flex items-center gap-1.5 px-3 py-1.5 border border-grey-300 text-grey-700 text-xs rounded-lg hover:bg-grey-50 transition font-medium"
          >
            <ScanLine className="w-3.5 h-3.5" />
            Check In
          </Link>
          {view === "manage" && (
            <>
              <button
                onClick={() => setShowQuickSetup(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-primary-600 hover:bg-primary-700 text-white text-xs rounded-lg transition font-semibold"
              >
                <Zap className="w-3.5 h-3.5" />
                Quick Setup
              </button>
              <button
                onClick={openCreate}
                className="flex items-center gap-1.5 px-3 py-1.5 border border-grey-300 text-grey-700 text-xs rounded-lg hover:bg-grey-50 transition font-medium"
              >
                <Plus className="w-3.5 h-3.5" />
                Custom
              </button>
            </>
          )}
        </div>
      </div>

      {/* Manage view */}
      {view === "manage" && (
        <>
          {isLoading ? (
            <div className="py-12 text-center text-sm text-grey-500">Loading ticket types...</div>
          ) : ticketTypes.length === 0 ? (
            <div className="bg-grey-50 border border-dashed border-grey-300 rounded-xl p-10 text-center">
              <Ticket className="w-12 h-12 text-grey-400 mx-auto mb-3" />
              <h3 className="font-medium text-grey-700 mb-1">No ticket types yet</h3>
              <p className="text-sm text-grey-500 mb-4">Create a ticket type to start selling attendee tickets for this event.</p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-2">
                <button
                  onClick={() => setShowQuickSetup(true)}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white text-sm rounded-xl hover:bg-primary-700 transition font-semibold"
                >
                  <Zap className="w-4 h-4" />
                  Quick Setup
                </button>
                <button
                  onClick={openCreate}
                  className="inline-flex items-center gap-2 px-4 py-2 border border-grey-300 text-grey-700 text-sm rounded-xl hover:bg-grey-50 transition font-medium"
                >
                  <Plus className="w-4 h-4" />
                  Custom Ticket Type
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              {ticketTypes.map(tt => {
                const remaining = tt.totalQuantity - (tt.soldCount || 0);
                return (
                  <div key={tt.id} className={`bg-white border rounded-xl p-4 ${!tt.isActive ? "opacity-60 border-grey-200" : "border-grey-200"}`}>
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h4 className="font-medium text-grey-900">{tt.name}</h4>
                          {!tt.isActive && (
                            <span className="px-2 py-0.5 text-xs bg-grey-100 text-grey-500 rounded-full">Inactive</span>
                          )}
                          {remaining === 0 && tt.isActive && (
                            <span className="px-2 py-0.5 text-xs bg-red-100 text-red-600 rounded-full">Sold Out</span>
                          )}
                        </div>
                        {tt.description && (
                          <p className="text-sm text-grey-500 mt-1">{tt.description}</p>
                        )}
                        <div className="flex items-center gap-4 mt-2 flex-wrap">
                          <span className="text-sm font-semibold text-grey-900">
                            {tt.price === 0 ? "Free" : formatCurrency(tt.price)}
                          </span>
                          <span className="text-xs text-grey-500">
                            {tt.soldCount || 0} sold / {tt.totalQuantity} total · {remaining} remaining
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <button
                          onClick={() => openEdit(tt)}
                          className="p-2 text-grey-500 hover:text-grey-700 hover:bg-grey-100 rounded-lg transition"
                          title="Edit"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        {tt.isActive && (
                          <button
                            onClick={() => handleDeactivate(tt.id)}
                            className="p-2 text-grey-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition"
                            title="Deactivate"
                          >
                            <ToggleRight className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* Sales view */}
      {view === "sales" && (
        salesLoading ? (
          <div className="py-12 text-center text-sm text-grey-500">Loading sales data...</div>
        ) : !sales ? (
          <div className="py-12 text-center text-sm text-grey-500">No sales data available.</div>
        ) : (
          <>
            {/* Summary stats */}
            <div className="grid grid-cols-3 gap-4 mb-6">
              <div className="bg-white border border-grey-200 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-1">
                  <Ticket className="w-4 h-4 text-grey-500" />
                  <span className="text-xs text-grey-500">Tickets Sold</span>
                </div>
                <p className="text-2xl font-semibold text-grey-900">{sales.totals.sold}</p>
              </div>
              <div className="bg-white border border-grey-200 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-1">
                  <DollarSign className="w-4 h-4 text-grey-500" />
                  <span className="text-xs text-grey-500">Gross Revenue</span>
                </div>
                <p className="text-2xl font-semibold text-grey-900">{formatCurrency(sales.totals.gross)}</p>
              </div>
              <div className="bg-white border border-grey-200 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-1">
                  <TrendingUp className="w-4 h-4 text-grey-500" />
                  <span className="text-xs text-grey-500">Your Net</span>
                </div>
                <p className="text-2xl font-semibold text-grey-900">{formatCurrency(sales.totals.net)}</p>
              </div>
            </div>

            {/* Purchases table */}
            {sales.purchases.length === 0 ? (
              <div className="bg-grey-50 border border-dashed border-grey-300 rounded-xl p-8 text-center">
                <Users className="w-10 h-10 text-grey-400 mx-auto mb-3" />
                <p className="text-grey-500 text-sm">No ticket purchases yet.</p>
              </div>
            ) : (
              <div className="bg-white border border-grey-200 rounded-xl overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-grey-50 border-b border-grey-200">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-grey-600">Buyer</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-grey-600">Ticket</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-grey-600">Price</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-grey-600">Date</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-grey-600">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-grey-100">
                    {sales.purchases.map(p => (
                      <tr key={p.id} className="hover:bg-grey-50">
                        <td className="px-4 py-3">
                          <p className="font-medium text-grey-900">{p.buyerName}</p>
                          <p className="text-xs text-grey-500">{p.buyerEmail}</p>
                        </td>
                        <td className="px-4 py-3 text-grey-700">{p.ticketTypeName}</td>
                        <td className="px-4 py-3 text-grey-700">{formatCurrency(p.priceAtPurchase)}</td>
                        <td className="px-4 py-3 text-grey-500 text-xs">
                          {p.createdAt?.toDate ? p.createdAt.toDate().toLocaleDateString() : "—"}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex px-2 py-0.5 text-xs rounded-full font-medium ${
                            p.status === 'completed' ? 'bg-green-100 text-green-700' :
                            p.status === 'refunded' ? 'bg-red-100 text-red-700' :
                            'bg-yellow-100 text-yellow-700'
                          }`}>
                            {p.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )
      )}

      </div>
    </div>
      {/* Create/Edit Form Modal */}
      {formOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-grey-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-grey-900">
                {editingId ? "Edit Ticket Type" : "New Ticket Type"}
              </h3>
              <button onClick={() => setFormOpen(false)} className="p-1 hover:bg-grey-100 rounded-lg transition">
                <X className="w-5 h-5 text-grey-500" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</div>
              )}

              <div>
                <label className="block text-sm font-medium text-grey-700 mb-1">Name <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  value={form.name}
                  onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                  placeholder="e.g. General Admission"
                  className="w-full px-3 py-2 border border-grey-300 rounded-lg focus:ring-2 focus:ring-grey-900 focus:border-transparent text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-grey-700 mb-1">Description</label>
                <textarea
                  value={form.description}
                  onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
                  rows={2}
                  placeholder="Optional details about this ticket type"
                  className="w-full px-3 py-2 border border-grey-300 rounded-lg focus:ring-2 focus:ring-grey-900 focus:border-transparent text-sm"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-grey-700 mb-1">Price (USD) <span className="text-red-500">*</span></label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.price}
                    onChange={e => setForm(p => ({ ...p, price: e.target.value }))}
                    placeholder="0.00"
                    className="w-full px-3 py-2 border border-grey-300 rounded-lg focus:ring-2 focus:ring-grey-900 focus:border-transparent text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-grey-700 mb-1">Quantity <span className="text-red-500">*</span></label>
                  <input
                    type="number"
                    min="1"
                    value={form.totalQuantity}
                    onChange={e => setForm(p => ({ ...p, totalQuantity: e.target.value }))}
                    placeholder="100"
                    className="w-full px-3 py-2 border border-grey-300 rounded-lg focus:ring-2 focus:ring-grey-900 focus:border-transparent text-sm"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-grey-700 mb-1">Sale Start</label>
                  <input
                    type="date"
                    value={form.saleStartDate}
                    onChange={e => setForm(p => ({ ...p, saleStartDate: e.target.value }))}
                    className="w-full px-3 py-2 border border-grey-300 rounded-lg focus:ring-2 focus:ring-grey-900 focus:border-transparent text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-grey-700 mb-1">Sale End</label>
                  <input
                    type="date"
                    value={form.saleEndDate}
                    onChange={e => setForm(p => ({ ...p, saleEndDate: e.target.value }))}
                    className="w-full px-3 py-2 border border-grey-300 rounded-lg focus:ring-2 focus:ring-grey-900 focus:border-transparent text-sm"
                  />
                </div>
              </div>

              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.isActive}
                  onChange={e => setForm(p => ({ ...p, isActive: e.target.checked }))}
                  className="w-4 h-4 rounded border-grey-300"
                />
                <span className="text-sm text-grey-700">Active (visible on public ticket page)</span>
              </label>
            </div>

            <div className="p-6 border-t border-grey-200 flex gap-3">
              <button
                onClick={() => setFormOpen(false)}
                className="flex-1 px-4 py-2 border border-grey-300 text-grey-700 rounded-lg hover:bg-grey-50 transition font-medium text-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex-1 px-4 py-2 bg-grey-900 text-white rounded-lg hover:bg-grey-800 transition font-medium text-sm disabled:opacity-50"
              >
                {saving ? "Saving..." : editingId ? "Save Changes" : "Create Ticket Type"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );


  return (
    <PremiumFeatureGate featureName="Ticketing">
      {content}
    </PremiumFeatureGate>
  );
};

export default Ticketing;
